import { FC } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PermitVerificationStatus } from "@services";

/** Four display states shared by the table badge (MS-486) and the edit section
 * (MS-488). near-expiry / expired are derived from the permit expiry date and
 * only occur once expiry-monitoring ships; V1 shows verified / unverified. */
export type VerificationDisplayState = "unverified" | "verified" | "nearExpiry" | "expired";

const NEAR_EXPIRY_DAYS = 30;

export const verificationDisplayState = (
  status?: number | null,
  expiryDate?: string | null
): VerificationDisplayState => {
  if (status !== PermitVerificationStatus.Verified) return "unverified";
  if (expiryDate) {
    const exp = new Date(expiryDate).getTime();
    const now = Date.now();
    if (exp < now) return "expired";
    if (exp - now < NEAR_EXPIRY_DAYS * 86_400_000) return "nearExpiry";
  }
  return "verified";
};

const STYLES: Record<VerificationDisplayState, { bg: string; color: string; key: string }> = {
  verified: { bg: "#DCFCE7", color: "#16A34A", key: "STATUS_VERIFIED" },
  unverified: { bg: "#F3F4F6", color: "#6B7280", key: "STATUS_UNVERIFIED" },
  nearExpiry: { bg: "#FEF3C7", color: "#B45309", key: "STATUS_NEAR_EXPIRY" },
  expired: { bg: "#FEE2E2", color: "#DC2626", key: "STATUS_EXPIRED" },
};

export const VerificationBadge: FC<{
  status?: number | null;
  expiryDate?: string | null;
  onClick?: () => void;
}> = ({ status, expiryDate, onClick }) => {
  const { t } = useTranslation();
  const state = verificationDisplayState(status, expiryDate);
  const s = STYLES[state];
  // Unverified / expired are actionable (open the verify modal — wired later).
  const clickable = !!onClick && (state === "unverified" || state === "expired");
  return (
    <Box
      component="span"
      onClick={clickable ? onClick : undefined}
      sx={{
        display: "inline-block",
        backgroundColor: s.bg,
        color: s.color,
        fontSize: 12,
        fontWeight: 700,
        borderRadius: "100px",
        px: "12px",
        py: "4px",
        whiteSpace: "nowrap",
        cursor: clickable ? "pointer" : "default",
      }}
    >
      {t(`PERMIT_VERIFICATION.${s.key}`)}
    </Box>
  );
};

export default VerificationBadge;
